import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Home,
  Plus,
  Search,
  MapPin,
  Users,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Settings,
  Edit,
  Trash2,
  MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { Animal, Enclosure } from '../LivestockManagementPage';
import { 
  getCardClasses, 
  getShadowClasses, 
  components, 
  colors,
  borders,
  spacing,
  getButtonClasses,
  getIconContainer
} from '@/components/business/design-system/businessDesignTokens';

interface EnclosuresTabProps {
  enclosures: Enclosure[];
  setEnclosures: React.Dispatch<React.SetStateAction<Enclosure[]>>;
  animals: Animal[];
}

// Proper Badge variant types
type BadgeVariant = "default" | "secondary" | "destructive" | "outline" | null | undefined;

export function EnclosuresTab({ enclosures, setEnclosures, animals }: EnclosuresTabProps) {
  const { t } = useTranslation('business', { keyPrefix: 'livestock.enclosuresTab' });
  const { t: tc } = useTranslation('common');

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [capacityFilter, setCapacityFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedEnclosure, setSelectedEnclosure] = useState<Enclosure | null>(null);
  
  // Form state for adding/editing enclosures
  const [formData, setFormData] = useState<Partial<Enclosure>>({
    name: '',
    type: 'Outdoor',
    size: 0,
    capacity: 0,
    currentAnimals: [],
    lastCleaned: '',
    maintenanceSchedule: {
      frequency: '',
      lastDone: '',
      nextDue: ''
    },
    environmentalNotes: ''
  });

  // Filter enclosures
  const filteredEnclosures = enclosures.filter(enclosure => {
    const matchesSearch = searchTerm === '' || 
      enclosure.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === 'all' || enclosure.type === typeFilter;
    
    let matchesCapacity = true;
    if (capacityFilter === 'available') {
      matchesCapacity = enclosure.currentAnimals.length < enclosure.capacity;
    } else if (capacityFilter === 'full') {
      matchesCapacity = enclosure.currentAnimals.length >= enclosure.capacity;
    } else if (capacityFilter === 'empty') {
      matchesCapacity = enclosure.currentAnimals.length === 0;
    }
    
    return matchesSearch && matchesType && matchesCapacity;
  });

  // Enclosure statistics
  const enclosureStats = {
    total: enclosures.length,
    occupied: enclosures.filter(e => e.currentAnimals.length > 0).length,
    available: enclosures.filter(e => e.currentAnimals.length < e.capacity).length,
    maintenanceDue: enclosures.filter(e => {
      if (!e.maintenanceSchedule.nextDue) return false;
      const dueDate = new Date(e.maintenanceSchedule.nextDue);
      const today = new Date();
      return dueDate <= today;
    }).length
  };

  const handleFormChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof Enclosure] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSaveEnclosure = () => {
    if (!formData.name || !formData.size || !formData.capacity) {
      alert('Please fill in required fields');
      return;
    }

    if (showEditDialog && selectedEnclosure) {
      // Update existing enclosure
      setEnclosures(prev => 
        prev.map(enclosure => 
          enclosure.id === selectedEnclosure.id 
            ? { ...enclosure, ...formData } as Enclosure
            : enclosure
        )
      );
    } else {
      // Add new enclosure
      const newEnclosure: Enclosure = {
        ...formData,
        id: `e${Date.now()}`,
        currentAnimals: formData.currentAnimals || []
      } as Enclosure;
      
      setEnclosures(prev => [...prev, newEnclosure]);
    }

    // Reset form and close dialog
    setFormData({
      name: '', type: 'Outdoor', size: 0, capacity: 0, currentAnimals: [],
      lastCleaned: '', maintenanceSchedule: { frequency: '', lastDone: '', nextDue: '' },
      environmentalNotes: ''
    });
    setShowAddDialog(false);
    setShowEditDialog(false);
    setSelectedEnclosure(null);
  };

  const handleDeleteEnclosure = (enclosureId: string) => {
    const enclosure = enclosures.find(e => e.id === enclosureId);
    if (enclosure && enclosure.currentAnimals.length > 0) {
      alert(t('cannotDeleteWithAnimals'));
      return;
    }
    
    if (confirm(t('confirmDelete'))) {
      setEnclosures(prev => prev.filter(enclosure => enclosure.id !== enclosureId));
    }
  };

  const openEditDialog = (enclosure: Enclosure) => {
    setSelectedEnclosure(enclosure);
    setFormData({ ...enclosure });
    setShowEditDialog(true);
  };

  const openViewDialog = (enclosure: Enclosure) => {
    setSelectedEnclosure(enclosure);
    setShowViewDialog(true);
  };

  const getTypeColor = (type: Enclosure['type']): BadgeVariant => {
    switch (type) {
      case 'Indoor': return 'secondary';
      case 'Outdoor': return 'default';
      case 'Mixed': return 'outline';
      default: return 'secondary';
    }
  };

  const getCapacityStatus = (enclosure: Enclosure): { status: string; color: BadgeVariant } => {
    const usage = enclosure.currentAnimals.length / enclosure.capacity;
    if (usage >= 1) return { status: t('statusFull'), color: 'destructive' };
    if (usage >= 0.8) return { status: t('statusNearFull'), color: 'secondary' };
    if (usage > 0) return { status: t('statusAvailable'), color: 'default' };
    return { status: t('statusEmpty'), color: 'outline' };
  };

  const getAnimalName = (animalId: string) => {
    const animal = animals.find(a => a.id === animalId);
    return animal ? animal.name : 'Unknown Animal';
  };

  const isMaintenanceDue = (enclosure: Enclosure) => {
    if (!enclosure.maintenanceSchedule.nextDue) return false;
    const dueDate = new Date(enclosure.maintenanceSchedule.nextDue);
    const today = new Date();
    return dueDate <= today;
  };

  return (
    <div className={spacing.sectionGap}>
      {/* Streamlined Header */}
      <div className="mb-6">
        <p className="text-sm text-slate-600">{t('description')}</p>
      </div>

      {/* Streamlined Stats and Actions */}
      <Card className={getCardClasses('basic')}>
        <CardContent className="p-4">
          {/* Compact Stats Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-4 text-sm">
                <div className="text-center">
                  <div className="text-lg font-bold text-slate-900">{enclosureStats.total}</div>
                  <div className="text-xs text-slate-600">{t('totalEnclosures')}</div>
                </div>
                <div className="text-center">
                  <div className={`text-lg font-bold text-${colors.primary.main}`}>{enclosureStats.occupied}</div>
                  <div className="text-xs text-slate-600">{t('occupied')}</div>
                </div>
                <div className="text-center">
                  <div className={`text-lg font-bold text-${colors.success.main}`}>{enclosureStats.available}</div>
                  <div className="text-xs text-slate-600">{t('available')}</div>
                </div>
                {enclosureStats.maintenanceDue > 0 && (
                  <div className="text-center">
                    <div className={`text-lg font-bold text-${colors.warning.main}`}>{enclosureStats.maintenanceDue}</div>
                    <div className="text-xs text-slate-600">{t('maintenanceDue')}</div>
                  </div>
                )}
              </div>
            </div>
            <Button onClick={() => setShowAddDialog(true)} className={getButtonClasses('success')}>
              <Plus className="w-4 h-4 mr-2" />
              {t('addEnclosure')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Compact Search and Filters */}
      <div className="space-y-4">
        {/* Compact Filters Row */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 w-full sm:w-auto">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 ${components.form.input}`}
              />
            </div>
            
            <div className="flex gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-28">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('allTypes')}</SelectItem>
                  <SelectItem value="Indoor">Indoor</SelectItem>
                  <SelectItem value="Outdoor">Outdoor</SelectItem>
                  <SelectItem value="Mixed">Mixed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={capacityFilter} onValueChange={setCapacityFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Capacity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('allCapacities')}</SelectItem>
                  <SelectItem value="empty">{t('statusEmpty')}</SelectItem>
                  <SelectItem value="available">{t('statusAvailable')}</SelectItem>
                  <SelectItem value="full">{t('statusFull')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Results Count and Clear Filters */}
        <div className="flex items-center justify-between text-sm py-2 border-t border-slate-100">
          <span className="text-slate-600">
            Showing {filteredEnclosures.length} of {enclosures.length} enclosures
          </span>
          {(searchTerm || typeFilter !== 'all' || capacityFilter !== 'all') && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setTypeFilter('all');
                setCapacityFilter('all');
              }}
              className="text-slate-500 hover:text-slate-700"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>


      {/* Enclosures Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEnclosures.length === 0 ? (
          <div className="col-span-full">
            <Card className={getCardClasses('basic')}>
              <CardContent className="text-center py-8">
                <Home className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">{t('noEnclosuresFound')}</h3>
                <p className="text-slate-600 mb-4">
                  {enclosures.length === 0 
                    ? t('createFirstEnclosure')
                    : t('adjustSearchCriteria')
                  }
                </p>
                <Button onClick={() => setShowAddDialog(true)} className={getButtonClasses('success')}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('createFirst')}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          filteredEnclosures.map(enclosure => {
            const capacityStatus = getCapacityStatus(enclosure);
            return (
              <Card key={enclosure.id} className={`${getCardClasses('basic')} hover:shadow-lg`}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{enclosure.name}</CardTitle>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge variant={getTypeColor(enclosure.type)}>
                          {enclosure.type}
                        </Badge>
                        <Badge variant={capacityStatus.color}>
                          {capacityStatus.status}
                        </Badge>
                        {isMaintenanceDue(enclosure) && (
                          <Badge variant="destructive" className="text-xs">
                            {t('maintenanceDueShort')}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openViewDialog(enclosure)}>
                          <MapPin className="w-4 h-4 mr-2" />
                          {tc('viewDetails')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEditDialog(enclosure)}>
                          <Edit className="w-4 h-4 mr-2" />
                          {tc('edit')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDeleteEnclosure(enclosure.id)}
                          className="text-red-600"
                          disabled={enclosure.currentAnimals.length > 0}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {tc('delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-slate-600">{t('size')}:</span>
                      <div className="font-medium">{enclosure.size.toLocaleString()} m²</div>
                    </div>
                    <div>
                      <span className="text-slate-600">{t('capacity')}:</span>
                      <div className="font-medium">
                        {enclosure.currentAnimals.length} / {enclosure.capacity}
                      </div>
                    </div>
                  </div>
                  
                  {enclosure.currentAnimals.length > 0 && (
                    <div>
                      <span className="text-slate-600 text-sm">{t('currentAnimals')}:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {enclosure.currentAnimals.slice(0, 3).map(animalId => (
                          <Badge key={animalId} variant="outline" className="text-xs">
                            {getAnimalName(animalId)}
                          </Badge>
                        ))}
                        {enclosure.currentAnimals.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{enclosure.currentAnimals.length - 3} {t('more')}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="text-xs text-slate-500 space-y-1">
                    {enclosure.lastCleaned && (
                      <div>{t('lastCleaned')}: {new Date(enclosure.lastCleaned).toLocaleDateString()}</div>
                    )}
                    {enclosure.maintenanceSchedule.nextDue && (
                      <div className={isMaintenanceDue(enclosure) ? 'text-amber-600 font-medium' : ''}>
                        {t('nextMaintenance')}: {new Date(enclosure.maintenanceSchedule.nextDue).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Add/Edit Enclosure Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setShowEditDialog(false);
          setSelectedEnclosure(null);
          setFormData({
            name: '', type: 'Outdoor', size: 0, capacity: 0, currentAnimals: [],
            lastCleaned: '', maintenanceSchedule: { frequency: '', lastDone: '', nextDue: '' },
            environmentalNotes: ''
          });
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {showEditDialog ? t('editEnclosure', { name: selectedEnclosure?.name }) : t('addNewEnclosure')}
            </DialogTitle>
            <DialogDescription>
              {showEditDialog 
                ? t('updateEnclosureInfo')
                : t('fillEnclosureDetails')
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-900">{t('basicInformation')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">{t('enclosureName')} *</Label>
                  <Input
                    id="name"
                    value={formData.name || ''}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder={t('enclosureNamePlaceholder')}
                    className={components.form.input}
                  />
                </div>
                <div>
                  <Label htmlFor="type">{t('type')} *</Label>
                  <Select value={formData.type} onValueChange={(value) => handleFormChange('type', value)}>
                    <SelectTrigger className={components.form.select}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Indoor">{t('indoor')}</SelectItem>
                      <SelectItem value="Outdoor">{t('outdoor')}</SelectItem>
                      <SelectItem value="Mixed">{t('mixed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="size">{t('size')} (m²) *</Label>
                  <Input
                    id="size"
                    type="number"
                    value={formData.size || ''}
                    onChange={(e) => handleFormChange('size', parseInt(e.target.value) || 0)}
                    placeholder={t('sizePlaceholder')}
                    className={components.form.input}
                  />
                </div>
                <div>
                  <Label htmlFor="capacity">{t('animalCapacity')} *</Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={formData.capacity || ''}
                    onChange={(e) => handleFormChange('capacity', parseInt(e.target.value) || 0)}
                    placeholder={t('capacityPlaceholder')}
                    className={components.form.input}
                  />
                </div>
              </div>
            </div>

            {/* Maintenance Schedule */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-slate-900">{t('maintenanceSchedule')}</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="frequency">{t('maintenanceFrequency')}</Label>
                  <Input
                    id="frequency"
                    value={formData.maintenanceSchedule?.frequency || ''}
                    onChange={(e) => handleFormChange('maintenanceSchedule.frequency', e.target.value)}
                    placeholder={t('frequencyPlaceholder')}
                    className={components.form.input}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="lastCleaned">{t('lastCleaned')}</Label>
                    <Input
                      id="lastCleaned"
                      type="date"
                      value={formData.lastCleaned || ''}
                      onChange={(e) => handleFormChange('lastCleaned', e.target.value)}
                      className={components.form.input}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastDone">{t('lastMaintenance')}</Label>
                    <Input
                      id="lastDone"
                      type="date"
                      value={formData.maintenanceSchedule?.lastDone || ''}
                      onChange={(e) => handleFormChange('maintenanceSchedule.lastDone', e.target.value)}
                      className={components.form.input}
                    />
                  </div>
                  <div>
                    <Label htmlFor="nextDue">{t('nextMaintenanceDue')}</Label>
                    <Input
                      id="nextDue"
                      type="date"
                      value={formData.maintenanceSchedule?.nextDue || ''}
                      onChange={(e) => handleFormChange('maintenanceSchedule.nextDue', e.target.value)}
                      className={components.form.input}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Environmental Notes */}
            <div>
              <Label htmlFor="environmentalNotes">{t('environmentalNotes')}</Label>
              <Textarea
                id="environmentalNotes"
                value={formData.environmentalNotes || ''}
                onChange={(e) => handleFormChange('environmentalNotes', e.target.value)}
                placeholder={t('environmentalNotesPlaceholder')}
                rows={3}
                className={components.form.textarea}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddDialog(false);
              setShowEditDialog(false);
            }} className={getButtonClasses('secondary')}>
              {tc('cancel')}
            </Button>
            <Button onClick={handleSaveEnclosure} className={getButtonClasses('success')}>
              {showEditDialog ? t('updateEnclosure') : t('addEnclosure')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Enclosure Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Home className={`w-5 h-5 text-${colors.success.main}`} />
              <span>{selectedEnclosure?.name}</span>
            </DialogTitle>
            <DialogDescription>{t('completeEnclosureDetails')}</DialogDescription>
          </DialogHeader>

          {selectedEnclosure && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">{t('basicInformation')}</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-slate-600">{t('type')}:</span> {selectedEnclosure.type}</div>
                    <div><span className="text-slate-600">{t('size')}:</span> {selectedEnclosure.size.toLocaleString()} m²</div>
                    <div><span className="text-slate-600">{t('capacity')}:</span> {selectedEnclosure.capacity} {t('animals')}</div>
                    <div><span className="text-slate-600">{t('currentOccupancy')}:</span> {selectedEnclosure.currentAnimals.length} {t('animals')}</div>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">{t('maintenance')}</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-slate-600">{t('frequency')}:</span> {selectedEnclosure.maintenanceSchedule.frequency || t('notSpecified')}</div>
                    <div><span className="text-slate-600">{t('lastCleaned')}:</span> {selectedEnclosure.lastCleaned ? new Date(selectedEnclosure.lastCleaned).toLocaleDateString() : t('notRecorded')}</div>
                    <div><span className="text-slate-600">{t('lastMaintenance')}:</span> {selectedEnclosure.maintenanceSchedule.lastDone ? new Date(selectedEnclosure.maintenanceSchedule.lastDone).toLocaleDateString() : t('notRecorded')}</div>
                    <div><span className="text-slate-600">{t('nextDue')}:</span> {selectedEnclosure.maintenanceSchedule.nextDue ? new Date(selectedEnclosure.maintenanceSchedule.nextDue).toLocaleDateString() : t('notScheduled')}</div>
                  </div>
                </div>
              </div>

              {selectedEnclosure.currentAnimals.length > 0 && (
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">{t('currentAnimals')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedEnclosure.currentAnimals.map(animalId => (
                      <Badge key={animalId} variant="outline">
                        {getAnimalName(animalId)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedEnclosure.environmentalNotes && (
                <div>
                  <h3 className="font-medium text-slate-900 mb-2">{t('environmentalNotes')}</h3>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-md">
                    {selectedEnclosure.environmentalNotes}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)} className={getButtonClasses('secondary')}>
              {tc('close')}
            </Button>
            <Button onClick={() => {
              setShowViewDialog(false);
              if (selectedEnclosure) openEditDialog(selectedEnclosure);
            }} className={getButtonClasses('primary')}>
              <Edit className="w-4 h-4 mr-2" />
              {t('editEnclosure')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 